import {Action} from '@reduxjs/toolkit'
import {JobTitleModel} from '../models/JobTitleModel'

export interface ActionWithPayload<T> extends Action {
  payload?: T
}

export const actionTypes = {
  JobTitlesRequested: '[Request JobTitles] Action',
  JobTitlesLoaded: '[Load JobTitles] JobTitles API',
  JobTitlesFailed: '[Load JobTitles] JobTitles API Failed',
  JobTitleRequested: '[Request JobTitle] Action',
  JobTitleLoaded: '[Load JobTitle] JobTitle API',
  JobTitleFailed: '[Load JobTitle] JobTitle API Failed',
  JobTitleCreated: '[Create JobTitle] Action',
  JobTitleUpdated: '[Update JobTitle] Action',
  JobTitleDeleted: '[Delete JobTitle] Action',
}

const initialJobTitleState: IJobTitleState = {
  jobTitles: [],
  jobTitle: undefined,
  loading: false,
  error: undefined,
}

export interface IJobTitleState {
  jobTitles: JobTitleModel[]
  jobTitle?: JobTitleModel
  loading: boolean
  error?: string
}

export const reducer = (state: IJobTitleState = initialJobTitleState, action: ActionWithPayload<IJobTitleState>) => {
  switch (action.type) {
    case actionTypes.JobTitlesRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.JobTitlesLoaded: {
      const jobTitles = action.payload?.jobTitles || []
      return {...state, jobTitles, loading: false, error: undefined}
    }

    case actionTypes.JobTitlesFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.JobTitleRequested: {
      return {...state, loading: true, error: undefined}
    }

    case actionTypes.JobTitleLoaded: {
      const jobTitle = action.payload?.jobTitle
      return {...state, jobTitle, loading: false, error: undefined}
    }

    case actionTypes.JobTitleFailed: {
      const error = action.payload?.error
      return {...state, loading: false, error}
    }

    case actionTypes.JobTitleCreated: {
      const jobTitle = action.payload?.jobTitle
      if (jobTitle) {
        return {...state, jobTitles: [...state.jobTitles, jobTitle], loading: false}
      }
      return state
    }

    case actionTypes.JobTitleUpdated: {
      const jobTitle = action.payload?.jobTitle
      if (jobTitle) {
        const updatedJobTitles = state.jobTitles.map(jt => (jt.id === jobTitle.id ? jobTitle : jt))
        return {...state, jobTitles: updatedJobTitles, jobTitle, loading: false}
      }
      return state
    }

    case actionTypes.JobTitleDeleted: {
      const jobTitleId = action.payload?.jobTitle?.id
      if (jobTitleId) {
        const filteredJobTitles = state.jobTitles.filter(jt => jt.id !== jobTitleId)
        return {...state, jobTitles: filteredJobTitles, loading: false}
      }
      return state
    }

    default:
      return state
  }
}

export const actions = {
  requestJobTitles: () => ({
    type: actionTypes.JobTitlesRequested,
  }),
  fulfillJobTitles: (jobTitles: JobTitleModel[]) => ({
    type: actionTypes.JobTitlesLoaded,
    payload: {jobTitles},
  }),
  failJobTitles: (error: string) => ({
    type: actionTypes.JobTitlesFailed,
    payload: {error},
  }),
  requestJobTitle: () => ({
    type: actionTypes.JobTitleRequested,
  }),
  fulfillJobTitle: (jobTitle: JobTitleModel) => ({
    type: actionTypes.JobTitleLoaded,
    payload: {jobTitle},
  }),
  failJobTitle: (error: string) => ({
    type: actionTypes.JobTitleFailed,
    payload: {error},
  }),
  createJobTitle: (jobTitle: JobTitleModel) => ({
    type: actionTypes.JobTitleCreated,
    payload: {jobTitle},
  }),
  updateJobTitle: (jobTitle: JobTitleModel) => ({
    type: actionTypes.JobTitleUpdated,
    payload: {jobTitle},
  }),
  deleteJobTitle: (jobTitle: JobTitleModel) => ({
    type: actionTypes.JobTitleDeleted,
    payload: {jobTitle},
  }),
}
